/**
 * Phase 2 Configuration Schema: Split Groups & Planogram Templates
 *
 * This schema extends the store configuration with:
 * 1. Split Groups - Sub-group priority by bandwidth (which sub-merchandise-groups appear at each bandwidth level)
 * 2. Planogram Templates - Physical fixture configuration for each category/meter position
 *
 * @see openspec/changes/add-excel-config-import/specs/split-groups/spec.md
 * @see openspec/changes/add-excel-config-import/specs/planogram-templates/spec.md
 */

import { createId } from "@paralleldrive/cuid2"
import { relations } from "drizzle-orm"
import {
  foreignKey,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  unique,
} from "drizzle-orm/pg-core"
import { configBandwidths, configurationVersions } from "./store-configuration-schema"

// =============================================================================
// Split Groups: Sub-Group Priority by Bandwidth
// =============================================================================

/**
 * Placement type for sub-group priorities
 */
export type SubGroupPlacementType = "wall" | "gondola"

/**
 * Sub-group priorities within a category at specific bandwidth levels
 *
 * This table defines which sub-merchandise-groups appear at each bandwidth
 * meter level and in what priority order. For example, at 7 meters of
 * Multimedia wall space, the priorities might be:
 * - Priority 1: Phone/Charging
 * - Priority 2: Cables
 * - Priority 3: Headphones
 *
 * Maps to: Split Groups Excel file (Split groups document 2022.xlsx)
 */
export const categorySubgroupPriorities = pgTable(
  "category_subgroup_priorities",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    /** The merchandise group (bandwidth) this priority belongs to */
    bandwidthId: text("bandwidth_id").notNull(),
    /** 'wall' or 'gondola' - different priorities for different placement types */
    placementType: text("placement_type").$type<SubGroupPlacementType>().notNull(),
    /** The bandwidth meter level this priority applies to (e.g., 5, 6, 7, 8, 9) */
    bandwidthMeters: integer("bandwidth_meters").notNull(),
    /** Sub-group display name (e.g., "phone accessories", "cables", "headphones") */
    subgroupName: text("subgroup_name").notNull(),
    /** Priority order (1 = highest priority, fills first) */
    priority: integer().notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "category_subgroup_priorities_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.bandwidthId],
      foreignColumns: [configBandwidths.id],
      name: "category_subgroup_priorities_bandwidth_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("category_subgroup_priorities_unique").on(
      table.configVersionId,
      table.bandwidthId,
      table.placementType,
      table.bandwidthMeters,
      table.subgroupName,
    ),
    index("category_subgroup_priorities_version_idx").on(table.configVersionId),
    index("category_subgroup_priorities_bandwidth_idx").on(table.bandwidthId),
    index("category_subgroup_priorities_lookup_idx").on(
      table.bandwidthId,
      table.placementType,
      table.bandwidthMeters,
    ),
  ],
)

// =============================================================================
// Planogram Templates: Fixture Types
// =============================================================================

/**
 * Fixture category classification
 */
export type FixtureCategory = "shelf" | "crossbar" | "basket" | "special"

/**
 * Fixture types master data
 *
 * Defines all available fixture types (Shelf 47, Crossbar/Hooks, Basket, etc.)
 * that can be used in planogram templates.
 *
 * Maps to: Data sheet in Action_Product Group Coding.xlsx
 */
export const fixtureTypes = pgTable(
  "fixture_types",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    /** Normalized code (e.g., "shelf_47", "crossbar_hooks", "basket") */
    code: text().notNull().unique(),
    /** Display name (e.g., "Shelf 47cm", "Crossbar/Hooks", "Basket") */
    displayName: text("display_name").notNull(),
    /** Category classification */
    category: text().$type<FixtureCategory>().notNull(),
    /** Height in centimeters (for shelves) */
    heightCm: integer("height_cm"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [index("fixture_types_code_idx").on(table.code)],
)

// =============================================================================
// Planogram Templates: Template Definitions
// =============================================================================

/**
 * Planogram placement type
 */
export type PlanogramPlacementType = "wall" | "gondola"

/**
 * Planogram templates
 *
 * Defines the template configuration for each merchandise group (bandwidth) at each meter position.
 * Templates specify the physical fixture layout (what fixtures go at what heights).
 *
 * Maps to: Merchandise group sheets (e.g., "Clothing Men (G)") in Action_Product Group Coding.xlsx
 * Used by: Blueprint Converter to generate fixture assignments for planograms
 */
export const planogramTemplates = pgTable(
  "planogram_templates",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    configVersionId: text("config_version_id").notNull(),
    /** Template code from Excel (e.g., "F5", "E2", "A3.3") */
    templateCode: text("template_code").notNull(),
    /** Bandwidth (merchandise group) this template belongs to */
    bandwidthId: text("bandwidth_id").notNull(),
    /** 'wall' or 'gondola' */
    placementType: text("placement_type").$type<PlanogramPlacementType>().notNull(),
    /** Sub-group name this template applies to (e.g., "Phone/Charging") */
    subgroupName: text("subgroup_name"),
    /** Meter position within the layout (1, 2, 3, etc.) */
    meterPosition: integer("meter_position").notNull(),
    /** The buying meters context this template is defined for (e.g., 7 for "Buying meters 7") */
    buyingMetersContext: integer("buying_meters_context").notNull(),
    /** Optional modifier (e.g., "+J" for hooks) */
    modifier: text(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "planogram_templates_version_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.bandwidthId],
      foreignColumns: [configBandwidths.id],
      name: "planogram_templates_bandwidth_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("planogram_templates_unique").on(
      table.configVersionId,
      table.templateCode,
      table.bandwidthId,
      table.placementType,
      table.meterPosition,
      table.buyingMetersContext,
    ),
    index("planogram_templates_version_idx").on(table.configVersionId),
    index("planogram_templates_bandwidth_idx").on(table.bandwidthId),
    index("planogram_templates_lookup_idx").on(
      table.bandwidthId,
      table.placementType,
      table.buyingMetersContext,
    ),
  ],
)

// =============================================================================
// Planogram Templates: Fixture Assignments
// =============================================================================

/**
 * Template fixture assignments
 *
 * Specifies which fixture goes at which height position within a template.
 * A template might have:
 * - Height 7: Shelf 47
 * - Height 12: Shelf 57
 * - Heights 18-36: Crossbar/Hooks
 *
 * Maps to: Cell values in category sheet templates
 */
export const templateFixtureAssignments = pgTable(
  "template_fixture_assignments",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    templateId: text("template_id").notNull(),
    /** Height position in centimeters (7, 12, 18, 24, 30, 36) */
    heightCm: integer("height_cm").notNull(),
    fixtureTypeId: text("fixture_type_id").notNull(),
    /** Optional modifiers for this assignment (e.g., ["+J"] for hooks) */
    modifiers: jsonb().$type<string[]>().default([]),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.templateId],
      foreignColumns: [planogramTemplates.id],
      name: "template_fixture_assignments_template_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.fixtureTypeId],
      foreignColumns: [fixtureTypes.id],
      name: "template_fixture_assignments_fixture_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    index("template_fixture_assignments_template_idx").on(table.templateId),
    index("template_fixture_assignments_height_idx").on(table.templateId, table.heightCm),
  ],
)

// =============================================================================
// Drizzle Relations
// =============================================================================

export const categorySubgroupPrioritiesRelations = relations(
  categorySubgroupPriorities,
  ({ one }) => ({
    configVersion: one(configurationVersions, {
      fields: [categorySubgroupPriorities.configVersionId],
      references: [configurationVersions.id],
    }),
    bandwidth: one(configBandwidths, {
      fields: [categorySubgroupPriorities.bandwidthId],
      references: [configBandwidths.id],
    }),
  }),
)

export const fixtureTypesRelations = relations(fixtureTypes, ({ many }) => ({
  templateAssignments: many(templateFixtureAssignments),
}))

export const planogramTemplatesRelations = relations(planogramTemplates, ({ one, many }) => ({
  configVersion: one(configurationVersions, {
    fields: [planogramTemplates.configVersionId],
    references: [configurationVersions.id],
  }),
  bandwidth: one(configBandwidths, {
    fields: [planogramTemplates.bandwidthId],
    references: [configBandwidths.id],
  }),
  fixtureAssignments: many(templateFixtureAssignments),
}))

export const templateFixtureAssignmentsRelations = relations(
  templateFixtureAssignments,
  ({ one }) => ({
    template: one(planogramTemplates, {
      fields: [templateFixtureAssignments.templateId],
      references: [planogramTemplates.id],
    }),
    fixtureType: one(fixtureTypes, {
      fields: [templateFixtureAssignments.fixtureTypeId],
      references: [fixtureTypes.id],
    }),
  }),
)
